import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Clock, CheckCircle2, Edit3 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

function BatchHistory({ user, onLogout }) {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [recipes, setRecipes] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingBatch, setEditingBatch] = useState(null);
  const [updateData, setUpdateData] = useState({ actual_yield: '', notes: '' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [batchesRes, recipesRes] = await Promise.all([
        axios.get(`${API}/batches`),
        axios.get(`${API}/recipes`),
      ]);

      setBatches(batchesRes.data);

      const recipeMap = {};
      recipesRes.data.forEach((r) => {
        recipeMap[r.id] = r;
      });
      setRecipes(recipeMap);
    } catch (error) {
      toast.error('Failed to fetch batch history');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    setUpdateData({
      actual_yield: batch.actual_yield || '',
      notes: batch.notes || '',
    });
  };

  const submitUpdate = async () => {
    if (!updateData.actual_yield) {
      toast.error('Please enter actual yield');
      return;
    }

    setUpdating(true);
    try {
      await axios.patch(`${API}/batches/${editingBatch.id}`, {
        actual_yield: parseFloat(updateData.actual_yield),
        notes: updateData.notes,
      });
      toast.success('Batch updated!');
      setEditingBatch(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update batch');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Pending';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="glass border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-to-dashboard-button"
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-bold text-lg text-gray-900">Batch History</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 pb-8">
        {batches.length === 0 ? (
          <div className="text-center py-20">
            <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No batches recorded yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {batches.map((batch) => {
              const recipe = recipes[batch.recipe_id];
              const variance =
                batch.actual_yield && batch.expected_yield
                  ? ((batch.actual_yield - batch.expected_yield) / batch.expected_yield) * 100
                  : null;

              return (
                <div
                  key={batch.id}
                  data-testid={`batch-${batch.id}`}
                  className="bg-white rounded-2xl p-5 shadow-md border border-gray-100"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-1">
                        {recipe?.name || 'Unknown Recipe'}
                      </h3>
                      <p className="text-sm text-gray-500">@{batch.staff_username}</p>
                      <p className="text-xs text-gray-400 mt-1">Prepared: {formatDate(batch.prepared_at)}</p>
                    </div>
                    {!batch.actual_yield && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditBatch(batch)}
                        data-testid={`edit-batch-${batch.id}-button`}
                        className="rounded-xl"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-purple-50 rounded-xl p-3">
                      <p className="text-xs text-purple-600 font-medium mb-1">Expected Yield</p>
                      <p className="text-xl font-bold text-purple-900">{batch.expected_yield}g</p>
                    </div>
                    <div
                      className={`rounded-xl p-3 ${
                        batch.actual_yield ? 'bg-green-50' : 'bg-gray-50'
                      }`}
                    >
                      <p
                        className={`text-xs font-medium mb-1 ${
                          batch.actual_yield ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        Actual Yield
                      </p>
                      <p
                        className={`text-xl font-bold ${
                          batch.actual_yield ? 'text-green-900' : 'text-gray-400'
                        }`}
                      >
                        {batch.actual_yield ? `${batch.actual_yield}g` : 'Pending'}
                      </p>
                    </div>
                  </div>

                  {variance !== null && (
                    <div className="mt-3 flex items-center justify-between p-3 rounded-xl bg-blue-50">
                      <span className="text-sm text-blue-700 font-medium">Variance</span>
                      <span
                        className={`text-sm font-bold ${
                          variance > 0 ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {variance > 0 ? '+' : ''}
                        {variance.toFixed(2)}%
                      </span>
                    </div>
                  )}

                  {batch.notes && (
                    <div className="mt-3 p-3 rounded-xl bg-gray-50">
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{batch.notes}</p>
                    </div>
                  )}

                  {batch.recorded_at && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                      <CheckCircle2 className="h-3 w-3" />
                      <span>Recorded: {formatDate(batch.recorded_at)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={!!editingBatch} onOpenChange={() => setEditingBatch(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Update Batch Yield</DialogTitle>
            <DialogDescription>Enter the actual yield for this batch</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="update-yield">Actual Yield (grams)</Label>
              <Input
                id="update-yield"
                data-testid="update-yield-input"
                type="number"
                step="0.01"
                placeholder="Enter actual yield"
                value={updateData.actual_yield}
                onChange={(e) => setUpdateData({ ...updateData, actual_yield: e.target.value })}
                className="h-12 mt-2 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="update-notes">Notes (Optional)</Label>
              <textarea
                id="update-notes"
                data-testid="update-notes-input"
                placeholder="Any observations..."
                value={updateData.notes}
                onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                className="w-full h-24 mt-2 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <Button
              onClick={submitUpdate}
              data-testid="submit-update-button"
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white font-semibold rounded-xl"
              disabled={!updateData.actual_yield || updating}
            >
              {updating ? 'Updating...' : 'Update Batch'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BatchHistory;